/**
 * An artifact's frame, from the page's side (wire.ts, `ARTIFACT_FRAME_PATH`).
 *
 * `html` and `svg` artifacts run what the model wrote, so they are drawn in a
 * document of their own: `/artifact.html`, framed with
 * `sandbox="allow-scripts"` and never `allow-same-origin`, and served with a
 * policy of its own (worker/lib/headers.ts). It has no origin, so it reads
 * none of Assist's cookies or storage and cannot reach this page or the API;
 * its network is the CDNs. `srcdoc` would have been simpler and is wrong: it
 * takes this page's policy, which would both block the artifact's CDNs and
 * hand it the page's own address as 'self'.
 *
 * The page builds the whole document here, the design system's tokens set
 * first in its head, and posts it to the frame once, when the frame says it
 * is ready. Once: a frame that has navigated itself elsewhere could say
 * ready again, and must be handed nothing more. Another version is a new
 * frame.
 */

import type { ArtifactKind, QuizQuestion } from '@wire';

/** The style element the tokens go in. The Worker keeps `<` out of them. */
const tokenStyle = (tokens: string): string => `<style data-design-tokens>${tokens.replace(/</g, '')}</style>`;

/** The tokens set before anything of the page's own, so its styles and
 *  scripts see them and can override them. */
export function withTokens(html: string, tokens: string | null): string {
  if (!tokens) return html;
  const style = tokenStyle(tokens);
  const head = /<head(\s[^>]*)?>/i.exec(html);
  if (head) return html.slice(0, head.index + head[0].length) + style + html.slice(head.index + head[0].length);
  const root = /<html(\s[^>]*)?>/i.exec(html);
  if (root) return `${html.slice(0, root.index + root[0].length)}<head>${style}</head>${html.slice(root.index + root[0].length)}`;
  const doctype = /^\s*<!doctype[^>]*>/i.exec(html);
  if (doctype) return doctype[0] + style + html.slice(doctype[0].length);
  return style + html;
}

/** The whole document a frame draws for an artifact of this kind. */
export function frameDocument(kind: Extract<ArtifactKind, 'html' | 'svg'>, content: string, tokens: string | null): string {
  if (kind === 'html') return withTokens(content, tokens);
  return withTokens(
    '<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;height:100%}body{display:grid;place-items:center}' +
      'body>svg{max-width:100%;max-height:100vh;height:auto}</style></head><body>' +
      content +
      '</body></html>',
    tokens
  );
}

/** The kinds drawn in the frame rather than by the page. */
export type FramedKind = Extract<ArtifactKind, 'html' | 'svg' | 'react'>;

/** Whether a kind is drawn in the frame rather than by the page. */
export const framed = (kind: ArtifactKind): kind is FramedKind => kind === 'html' || kind === 'svg' || kind === 'react';

/** The document for any framed kind: a `react` one compiled first
 *  (artifactReact.ts), loaded only when one is shown. */
export async function documentFor(kind: FramedKind, content: string, tokens: string | null): Promise<string> {
  if (kind !== 'react') return frameDocument(kind, content, tokens);
  const { compileReact, reactDocument } = await import('./artifactReact');
  return reactDocument(await compileReact(content), tokens);
}

/**
 * Hands `html` to the frame `frame` once, when it says it is ready. Returns
 * the listener's removal. Messages from anything but that frame's window,
 * and any after the first ready, are ignored.
 */
export function deliver(win: Window, frame: () => Window | null, html: string): () => void {
  let sent = false;
  const onMessage = (e: MessageEvent) => {
    const target = frame();
    if (sent || !target || e.source !== target) return;
    if ((e.data as { type?: unknown } | null)?.type !== 'ready') return;
    sent = true;
    // The frame has no origin to name: '*' is the only target there is.
    target.postMessage({ type: 'render', html }, '*');
  };
  win.addEventListener('message', onMessage);
  return () => win.removeEventListener('message', onMessage);
}

/** A quiz artifact's questions, or null when its content is not one. */
export function readQuiz(content: string): QuizQuestion[] | null {
  try {
    const questions = (JSON.parse(content) as { questions?: unknown } | null)?.questions;
    if (!Array.isArray(questions)) return null;
    const ok = questions.every((x) => {
      const q = (x ?? {}) as Record<string, unknown>;
      return typeof q.q === 'string' && typeof q.a === 'string' && Array.isArray(q.wrong) && q.wrong.every((w) => typeof w === 'string');
    });
    return ok ? (questions as QuizQuestion[]) : null;
  } catch {
    return null;
  }
}
